import { toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/auth-context';
import api from '@/libs/axiosInstance';
import { useUpgradeModalStore } from '@/stores/upgrade-modal-store';
import type { ApiResponse, SwitchBrandResponse } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export const useSwitchBrandAction = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { refreshCurrentUser } = useAuth();
  const navigate = useNavigate();

  return useMutation<ApiResponse<SwitchBrandResponse>, Error, string>({
    mutationFn: async (brandId: string) => {
      const response = await api.post(`/dashboard/brands/${brandId}/switch`);
      return response.data;
    },
    onSuccess: async (response) => {
      const brandName = response.data?.currentBrand?.name;
      useUpgradeModalStore.getState().closeUpgradeModal();

      // A read started before this POST can still describe the previous brand.
      await refreshCurrentUser({ force: true });

      // Clear ALL query cache - brand switch affects all brand-specific data
      queryClient.clear();

      toaster.success({
        title: t('brandMenu.switchSuccess'),
        description: t('brandMenu.switchSuccessDescription', { brandName }),
      });

      // Navigate to dashboard after brand switch
      // This is the cleanest UX because:
      // 1. All data on the new brand context is fresh
      // 2. Pages using React Router loaders (settings, programs) get reloaded properly
      // 3. The user starts fresh on the new brand's dashboard
      navigate('/dashboard', { replace: true });
    },
    onError: () => {
      toaster.error({
        title: t('common.error'),
        description: t('brandMenu.switchError'),
      });
    },
  });
};
